import {
  DRAW_PROTOCOL_VERSION,
  type DrawClient,
  type DrawEvent,
  type DrawJoinOpts,
  type DrawRoomState,
  type DrawSettings,
} from "./types";

/** WebSocket transport → DrawRoom DO at /draw/ws/<CODE>. Same shape as the
 *  Quizzle transport (versioned hello, 1.5s heartbeat + resync, reconnect). */
const HEARTBEAT_MS = 1500;

export class DrawTransport implements DrawClient {
  private ws: WebSocket | null = null;
  private url = "";
  private listeners: Array<(e: DrawEvent) => void> = [];
  private hello: DrawJoinOpts | null = null;
  private heartbeat: number | null = null;
  private reconnects = 0;

  async join(opts: DrawJoinOpts): Promise<DrawRoomState> {
    this.hello = opts;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/draw/ws/${opts.code}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("DrawIt server didn't respond")), 6000);
      const off = this.on((e) => {
        if (e.type === "state") {
          const joined = opts.role === "host" || e.state.players.some((p) => p.id === opts.me.id);
          if (joined) { clearTimeout(timer); off(); resolve(e.state); }
        } else if (e.type === "error") {
          clearTimeout(timer); off(); reject(new Error(e.message));
        }
      });
      this.connect();
    });
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnects = 0;
      if (this.hello) {
        const h = this.hello;
        this.send({ type: "hello", v: DRAW_PROTOCOL_VERSION, role: h.role, me: h.me, settings: h.settings });
      }
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") this.emit({ type: "state", state: msg.state });
        else if (msg.type === "error") this.emit({ type: "error", message: msg.message ?? "DrawIt error" });
        else if (msg.type === "stroke-point") this.emit({ type: "stroke-point", x: msg.x, y: msg.y, newStroke: !!msg.newStroke, color: msg.color, width: msg.width });
        else if (msg.type === "clear") this.emit({ type: "clear" });
        else if (msg.type === "guess-feedback") this.emit({ type: "guess-feedback", correct: !!msg.correct, delta: msg.delta, text: msg.text });
        else if (msg.type === "correct-announce") this.emit({ type: "correct-announce", playerId: msg.playerId, displayName: msg.displayName });
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      this.ws = null;
      this.stopHeartbeat();
      if (this.reconnects < 6 && this.hello) {
        this.reconnects++;
        setTimeout(() => this.connect(), Math.min(2000, 200 * 2 ** this.reconnects));
      }
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => this.send({ type: "ping", at: Date.now() }), HEARTBEAT_MS);
  }
  private stopHeartbeat() {
    if (this.heartbeat != null) { window.clearInterval(this.heartbeat); this.heartbeat = null; }
  }

  setSettings(settings: Partial<DrawSettings>): void { this.send({ type: "set-settings", settings }); }
  start(pool: string[]): void { this.send({ type: "start", pool }); }
  backToLobby(): void { this.send({ type: "back-to-lobby" }); }
  chooseWord(index: number): void { this.send({ type: "choose-word", index }); }
  strokePoint(x: number, y: number, newStroke: boolean, color?: string, width?: number): void {
    this.send({ type: "stroke-point", x, y, newStroke, color, width });
  }
  clearCanvas(): void { this.send({ type: "clear-canvas" }); }
  undoStroke(): void { this.send({ type: "undo-stroke" }); }
  guess(text: string): void { this.send({ type: "guess", text }); }
  endRoundNow(): void { this.send({ type: "end-round-now" }); }
  nextRound(drawerId?: string): void { this.send({ type: "next-round", drawerId }); }

  on(cb: (e: DrawEvent) => void): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter((l) => l !== cb); };
  }
  leave(): void {
    this.send({ type: "leave" });
    this.stopHeartbeat();
    this.hello = null;
    if (this.ws) { try { this.ws.close(); } catch { /* ignore */ } this.ws = null; }
    this.listeners = [];
  }

  private send(msg: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(msg)); } catch { /* reconnect handles it */ }
  }
  private emit(e: DrawEvent) { for (const l of this.listeners) l(e); }
}
